import { create } from 'zustand';
import { getSocket } from '../lib/socket';
import { useChatStore } from './chat';
import { useAuthStore } from './auth';
import type { Message, Task } from '../lib/api';

export type NotifType = 'message' | 'thread' | 'task';

export interface AppNotification {
  id: string;
  type: NotifType;
  title: string;
  body: string;
  threadId?: string;
  taskId?: string;
  timestamp: string;
  read: boolean;
}

interface NotificationsState {
  items: AppNotification[];
  unreadCount: number;
  setupSocketListeners: () => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
  requestPermission: () => void;
}

function fireBrowserNotif(title: string, body: string) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const n = new Notification(title, { body, icon: '/logo.svg' });
  n.onclick = () => { window.focus(); n.close(); };
}

function push(set: (fn: (s: NotificationsState) => Partial<NotificationsState>) => void, notif: AppNotification) {
  set((s) => ({
    items: [notif, ...s.items].slice(0, 50),
    unreadCount: s.unreadCount + 1,
  }));
}

export const useNotificationsStore = create<NotificationsState>((set) => ({
  items: [],
  unreadCount: 0,

  requestPermission: () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  },

  setupSocketListeners: () => {
    const socket = getSocket();

    socket.on('message:new', ({ threadId, message }: { threadId: string; message: Message }) => {
      const { activeThreadId, threads } = useChatStore.getState();
      if (threadId === activeThreadId) return;

      const thread = threads.find((t) => t.id === threadId);
      if (!thread) return;

      const threadName = thread.name ?? thread.client_account_name ?? 'New message';
      const sender = message.sender_contact_name ?? message.sender_name ?? '';
      const preview = (sender ? `${sender}: ` : '') + (message.content || '[media]');

      fireBrowserNotif(threadName, preview.slice(0, 80));
      push(set, {
        id: `msg-${message.id}`,
        type: 'message',
        title: threadName,
        body: preview.slice(0, 120),
        threadId,
        timestamp: message.created_at,
        read: false,
      });
    });

    socket.on('thread:new', ({ threadId }: { threadId: string }) => {
      fireBrowserNotif('New client message', 'A new client has started a conversation');
      push(set, {
        id: `thread-${threadId}-${Date.now()}`,
        type: 'thread',
        title: 'New client message',
        body: 'A new client has started a conversation',
        threadId,
        timestamp: new Date().toISOString(),
        read: false,
      });
    });

    socket.on('task:created', (task: Task) => {
      const currentUser = useAuthStore.getState().user;
      if (task.assigned_to !== currentUser?.id) return;

      fireBrowserNotif('New task assigned to you', task.title);
      push(set, {
        id: `task-${task.id}`,
        type: 'task',
        title: 'New task assigned to you',
        body: task.title,
        taskId: task.id,
        threadId: task.thread_id ?? undefined,
        timestamp: task.created_at,
        read: false,
      });
    });
  },

  markAllRead: () =>
    set((s) => ({
      items: s.items.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  remove: (id) =>
    set((s) => {
      const removed = s.items.find((n) => n.id === id);
      return {
        items: s.items.filter((n) => n.id !== id),
        unreadCount: removed && !removed.read ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
      };
    }),

  clear: () => set({ items: [], unreadCount: 0 }),
}));
